/**
 * Luật thuần của domain thương hiệu (ADR-0097): chuẩn hoá và kiểm hostname khi gõ, đoán domain gốc để cảnh báo,
 * thao tác nào hợp với trạng thái, và bản ghi DNS khách cần tạo. Không gọi HTTP, không chạm React.
 *
 * Server mới là nơi quyết: hostname bị từ chối vì là domain của nền tảng, domain công cộng hay đang được giữ chỗ
 * thì lỗi của server hiện dưới ô. Ở đây chỉ chặn sớm cái sai rõ ràng để người dùng không phải chờ một vòng gọi.
 */
import type { CustomHost } from '../types';

/** Độ dài tối đa của hostname (RFC 1035), như `maxLength` của `CustomHostCreate`. */
export const HOSTNAME_TOI_DA = 253;

/**
 * Hostname người dùng dán vào thường kèm `https://`, đường dẫn, cổng hay dấu chấm cuối: bỏ đi rồi mới kiểm, giống
 * cách server chuẩn hoá. Tên có dấu (IDN) để nguyên; server chuyển punycode.
 */
export function chuanHoaHostname(raw: string): string {
  let h = raw.trim().toLowerCase();
  h = h.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
  h = h.split(/[/?#]/)[0] ?? '';
  h = h.replace(/:\d*$/, '');
  return h.replace(/\.+$/, '');
}

export type LoiHostname = 'trong' | 'dai' | 'ip' | 'sai' | 'thieu-cham';

const NHAN = /^(?!-)[a-z0-9¡-￿-]{1,63}(?<!-)$/;
const IPV4 = /^\d{1,3}(\.\d{1,3}){3}$/;

/** Lỗi cú pháp của hostname đã chuẩn hoá, hoặc `null`. */
export function kiemHostname(host: string): LoiHostname | null {
  if (host === '') return 'trong';
  if (host.length > HOSTNAME_TOI_DA) return 'dai';
  if (IPV4.test(host) || host.includes(':') || host.startsWith('[')) return 'ip';
  const nhan = host.split('.');
  if (nhan.length < 2) return 'thieu-cham';
  if (!nhan.every((n) => NHAN.test(n))) return 'sai';
  if (/^\d+$/.test(nhan[nhan.length - 1] ?? '')) return 'sai';
  return null;
}

/**
 * Hậu tố hai nhãn mà dưới nó mới là domain của khách (`congtyabc.com.vn`). Không phải danh sách hậu tố công cộng
 * đầy đủ: chỉ đủ cho máy chủ giả và phần lớn trường hợp ở Việt Nam; giao diện thật dùng `apex` của server.
 */
const HAU_TO_HAI_NHAN = new Set([
  'com.vn', 'net.vn', 'org.vn', 'edu.vn', 'gov.vn', 'ac.vn', 'biz.vn', 'info.vn', 'name.vn', 'pro.vn',
  'health.vn', 'int.vn', 'id.vn', 'io.vn', 'co.uk', 'org.uk', 'com.au', 'co.jp', 'com.sg', 'com.cn', 'co.kr',
]);

/** Hostname là domain gốc (không đặt CNAME được), đoán theo số nhãn. */
export function laDomainGoc(host: string): boolean {
  const nhan = host.split('.');
  if (nhan.length === 2) return true;
  return nhan.length === 3 && HAU_TO_HAI_NHAN.has(nhan.slice(1).join('.'));
}

export interface ThaoTacDiaChi {
  kiemDns: boolean;
  kichHoat: boolean;
  tamNgung: boolean;
  /** Quản trị nền tảng khoá domain tổ chức đã tự tạm ngưng (gọi `suspend`, server chỉ đặt `platform_locked`). */
  khoa: boolean;
  go: boolean;
}

/**
 * Thao tác hợp với trạng thái (ADR-0097 mục 2, 4): kích hoạt từ `DA_XAC_MINH` hoặc mở lại từ `TAM_NGUNG`, tạm ngưng
 * chỉ khi `DANG_DUNG`, gỡ được mọi lúc trừ khi đã gỡ. Domain quản trị nền tảng đang khoá (`platform_locked`) thì người
 * xem ở cổng tổ chức không kích hoạt lại, cũng không gỡ được (server trả `403`), nên hai mục đó ẩn (ADR-UI-0005).
 * `laNenTang`: người xem ở cổng nền tảng, thêm mục khoá domain tổ chức đã tự tạm ngưng mà chưa khoá. Quyền sửa của
 * người xem xét riêng ở nơi gọi.
 */
export function thaoTacDuoc(
  h: Pick<CustomHost, 'status' | 'platform_locked'>,
  laNenTang: boolean,
): ThaoTacDiaChi {
  const biKhoa = h.platform_locked && !laNenTang;
  return {
    kiemDns: h.status !== 'DA_GO',
    kichHoat: !biKhoa && (h.status === 'DA_XAC_MINH' || h.status === 'TAM_NGUNG'),
    tamNgung: h.status === 'DANG_DUNG',
    khoa: laNenTang && h.status === 'TAM_NGUNG' && !h.platform_locked,
    go: !biKhoa && h.status !== 'DA_GO',
  };
}

/** Khối hướng dẫn DNS hiện khi khách còn việc phải làm: chờ DNS, hoặc lần kiểm gần nhất chưa đạt. */
export const canHuongDanDns = (h: Pick<CustomHost, 'status' | 'dns_error'>): boolean =>
  h.status === 'CHO_DNS' || (h.status !== 'DA_GO' && h.dns_error !== null);

export interface BanGhiDns {
  loai: 'TXT' | 'CNAME' | 'A' | 'AAAA';
  ten: string;
  giaTri: string;
}

/**
 * Bản ghi khách cần tạo: TXT chứng minh sở hữu, rồi CNAME về đích của nền tảng — hoặc A/AAAA về IP của nền tảng
 * khi là domain gốc hay nền tảng chưa có đích CNAME.
 */
export function banGhiCanTao(h: CustomHost): BanGhiDns[] {
  const ds: BanGhiDns[] = [{ loai: 'TXT', ten: h.txt_record_name, giaTri: h.verify_token }];
  if (!h.apex && h.cname_target) {
    ds.push({ loai: 'CNAME', ten: h.hostname, giaTri: h.cname_target });
    return ds;
  }
  for (const ip of h.target_ips) {
    ds.push({ loai: ip.includes(':') ? 'AAAA' : 'A', ten: h.hostname, giaTri: ip });
  }
  return ds;
}
